/**
 * AI Compliance (A2P 10DLC) templates for campaign registration.
 * Used as MessageSamples when creating Twilio Campaign; must be 20-1024 chars each,
 * include clear sender identification and opt-out in every message.
 */

const APP_NAME = 'Nurture Buddy.ai';

/**
 * Exact SMS consent disclosure shown on the public agent contact form checkbox.
 * Must match SSR HTML and message_flow quote byte-for-byte (TCR compares to live URL).
 *
 * MARKETING campaigns only — do not mention appointment reminders or other
 * transactional/informational message types here (TCR error 30913).
 */
export function getSmsConsentDisclosure(brandName) {
  const brand = (brandName && String(brandName).trim()) || 'the business';
  return (
    `Optional: By checking this box, I agree to receive recurring marketing and promotional SMS from ${brand} at the phone number provided ` +
    `about real estate services including property updates, market news, and follow-up communications. ` +
    `Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply. ` +
    `Reply STOP to opt out. Reply HELP for support.`
  );
}

/**
 * Sender name for campaign sample messages. TCR prefers a recognizable sender;
 * very long legal entity names in every sample often fail vetting (error 30893).
 */
export function getCampaignSenderName(brandName, fullName) {
  const brand = String(brandName || '').trim();
  const full = String(fullName || '').trim();
  if (brand.length > 35 && full) return full;
  return brand || full || 'my office';
}

/**
 * Public compliance URLs for an agent's opt-in flow.
 */
export function getAgentComplianceUrls(agentId, appBaseUrl) {
  const base = appBaseUrl ? String(appBaseUrl).trim().replace(/\/$/, '') : '';
  if (!base || !agentId) {
    return { optInPageUrl: null, privacyPolicyUrl: null, termsAndConditionsUrl: null };
  }
  return {
    optInPageUrl: `${base}/p/${agentId}`,
    privacyPolicyUrl: `${base}/p/${agentId}/privacy`,
    termsAndConditionsUrl: `${base}/p/${agentId}/terms`,
  };
}

export function getCampaignMessageSamples(brandOrAgentName = 'my office') {
  const name = String(brandOrAgentName || '').replace(/\[Agent Name\]|\[Brand Name\]/g, '').trim() || 'my office';
  const footer = 'Reply STOP to opt out. Reply HELP for help.';
  return [
    `Hi, this is ${name}. Just checking in — hope things are going well on your end. Let me know if you have any real estate questions. ${footer}`,
    `This is ${name}. I wanted to share a quick market update for your area — values have shifted lately. Reply if you'd like the details. ${footer}`,
    `${name} here — a new listing just came on the market that may match what you were looking for. Want me to send the details? ${footer}`,
    `Quick note from ${name}: inventory in your area has changed this month. Happy to share what's available if you're still exploring. ${footer}`,
    `Hi, it's ${name}. Thinking of you — hope everything is going well! Reach out anytime you have real estate questions or need a referral. ${footer}`,
  ];
}

export function getCampaignDescription(brandName, optInPageUrl, usAppToPersonUsecase = 'MARKETING') {
  const brand = (brandName && String(brandName).trim()) || 'the registered business';
  const isMarketing = usAppToPersonUsecase !== 'SOLE_PROPRIETOR';
  const programType = isMarketing ? 'marketing' : 'customer care';
  const optInNote = ` Consumers opt in on ${brand}'s public web contact form by checking an optional SMS consent checkbox.`;
  return (
    `This Campaign will send ${programType} SMS messages from ${brand} to consumers who have opted in to receive text messages from ${brand}.` +
    ` Intended recipients are real estate buyers, sellers, and past clients who requested property information, market updates, or ongoing communication.` +
    ` Messages include friendly client check-ins, local market updates, new listing alerts, and follow-up communications.` +
    ` Message frequency varies. Recipients may reply STOP to opt out or HELP for assistance.${optInNote}`
  ).slice(0, 4096);
}

/** @deprecated Use getCampaignDescription() */
export const AI_COMPLIANCE_CAMPAIGN_DESCRIPTION = getCampaignDescription('the registered business', null);

/**
 * Message flow / opt-in description (40-2048 chars) for Twilio UsAppToPerson.
 */
export function getMessageFlowWithComplianceUrls(brandName, agentId, appBaseUrl) {
  const brand = (brandName && String(brandName).trim()) || 'the business';
  const disclosure = getSmsConsentDisclosure(brand);
  const { optInPageUrl, privacyPolicyUrl, termsAndConditionsUrl } = getAgentComplianceUrls(agentId, appBaseUrl);

  if (!optInPageUrl || !privacyPolicyUrl || !termsAndConditionsUrl) {
    return (
      `End users opt in on ${brand}'s public web contact form by checking an optional SMS consent checkbox. ` +
      `The checkbox displays: "${disclosure}" Submitting the form does not require SMS consent. ` +
      `After opting in, consumers see a confirmation page stating they are enrolled in recurring marketing SMS from ${brand}. ` +
      `Consumers may opt out at any time by replying STOP, or reply HELP for help.`
    ).slice(0, 2048);
  }

  return (
    `End users visit ${brand}'s public contact page at ${optInPageUrl}, a web opt-in form operated for ${brand} on the ${APP_NAME} messaging platform, and enter their name, email, and mobile phone number. ` +
    `They may submit the contact form without checking the SMS consent checkbox — SMS consent is optional and not required to submit. ` +
    `To receive SMS, the consumer must separately check an unchecked optional marketing SMS consent checkbox agreeing to receive recurring marketing and promotional text messages from ${brand}. ` +
    `The checkbox displays this exact disclosure: "${disclosure}" ` +
    `Below the form the page states: message frequency varies, message and data rates may apply. ` +
    `After submitting with SMS consent checked, consumers see a confirmation page confirming enrollment in recurring marketing SMS from ${brand}, that message frequency varies, message and data rates may apply, and that they may reply STOP to opt out or HELP for help. ` +
    `Terms and Conditions: ${termsAndConditionsUrl}. ` +
    `Privacy Policy: ${privacyPolicyUrl} (states mobile numbers are not shared with third parties or affiliates for marketing or promotional purposes). ` +
    `No SMS messages are sent unless the consumer checks this optional box. ` +
    `Consumers may opt out at any time by replying STOP, after which no further messages will be sent.`
  ).slice(0, 2048);
}

export const AI_COMPLIANCE_OPT_IN_SUMMARY =
  'End users opt in via the agent\'s public web contact form by checking an optional SMS consent checkbox. Submitting the contact form does not require SMS consent. Privacy Policy and Terms links are shown on the form.';

export const AI_COMPLIANCE_MESSAGE_FLOW = getMessageFlowWithComplianceUrls('the business', null, null);

export const AI_COMPLIANCE_SAMPLE_MESSAGES = [
  {
    id: 'check-in',
    label: 'Friendly check-in',
    body: 'Hi, this is [Your Name]. Just checking in — hope things are going well. Let me know if you have any real estate questions. Reply STOP to opt out. Reply HELP for help.',
  },
  {
    id: 'market-update',
    label: 'Market update',
    body: "This is [Your Name]. I wanted to share a quick market update for your area. Reply if you'd like the details. Reply STOP to opt out. Reply HELP for help.",
  },
  {
    id: 'listing-alert',
    label: 'New listing alert',
    body: '[Your Name] here — a new listing just came on the market that may match what you were looking for. Want me to send the details? Reply STOP to opt out. Reply HELP for help.',
  },
];
